import isGroupedItems from './is-grouped-items';

function buildItemList(items, visibleIds, builtItems) {
  return items
    .filter((item) => visibleIds.has(item.id))
    .map((item) => builtItems.get(item.id) || item);
}

function buildGroupList(groups, visibleIds, builtItems) {
  return groups
    .map((group) => ({
      ...group,
      items: buildItemList(group.items, visibleIds, builtItems),
    }))
    .filter((group) => group.items.length > 0);
}

function filterTree({ items, query, searchValues, descendantItemIds, itemAncestors, idToItem }) {
  const lowerQuery = query.toLowerCase().trim();

  if (!lowerQuery) {
    return { visibleItems: items, autoExpandedIds: new Set() };
  }

  const matchedIds = new Set();
  const visibleIds = new Map(); // id -> depth
  const autoExpandedIds = new Set();

  searchValues.forEach((lowerValue, id) => {
    if (lowerValue.includes(lowerQuery)) {
      matchedIds.add(id);
    }
  });

  matchedIds.forEach((id) => {
    visibleIds.set(id, itemAncestors.get(id)?.length ?? 0);

    const subtree = descendantItemIds.get(id);

    if (subtree) {
      autoExpandedIds.add(id);

      subtree.forEach((sid) => {
        visibleIds.set(sid, itemAncestors.get(sid)?.length ?? 0);

        if (descendantItemIds.has(sid)) {
          autoExpandedIds.add(sid);
        }
      });
    }

    const ancestors = itemAncestors.get(id);

    if (ancestors) {
      ancestors.forEach((aid) => {
        visibleIds.set(aid, itemAncestors.get(aid)?.length ?? 0);
        autoExpandedIds.add(aid);
      });
    }
  });

  // Build filtered items bottom-up (deepest first)
  const builtItems = new Map();

  Array.from(visibleIds.entries())
    .sort(([, depthA], [, depthB]) => depthB - depthA)
    .forEach(([id]) => {
      const item = idToItem.get(id);

      if (!item) return;

      if (matchedIds.has(id) && descendantItemIds.has(id)) {
        builtItems.set(id, item);
        return;
      }

      if (!item.items || item.items.length === 0) {
        builtItems.set(id, item);
        return;
      }

      if (isGroupedItems(item.items)) {
        builtItems.set(id, { ...item, items: buildGroupList(item.items, visibleIds, builtItems) });
        return;
      }

      builtItems.set(id, {
        ...item,
        items: buildItemList(item.items, visibleIds, builtItems),
      });
    });

  const visibleItems = isGroupedItems(items)
    ? buildGroupList(items, visibleIds, builtItems)
    : buildItemList(items, visibleIds, builtItems);

  return {
    visibleItems,
    autoExpandedIds,
  };
}

export default filterTree;
